import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { elevation } from '@karnott/theme';

function useHover<ElementType extends HTMLElement>(
  disabled = false,
): [ref: RefObject<ElementType | null>, hovered: boolean] {
  const [value, setValue] = useState(false);

  const ref = useRef<ElementType>(null);

  const handleMouseEnter = useCallback(() => !disabled && setValue(true), [disabled]);
  const handleMouseLeave = useCallback(() => !disabled && setValue(false), [disabled]);

  useEffect(() => {
    const node = ref.current;
    if (node) {
      node.addEventListener('mouseenter', handleMouseEnter);
      node.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        node.removeEventListener('mouseenter', handleMouseEnter);
        node.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
    return () => {};
  }, [handleMouseEnter, handleMouseLeave]);

  return [ref, value];
}

function useOpenCloseState(isOpen = false): [opened: boolean, open: () => void, close: () => void, toggle: () => void] {
  const [opened, setOpened] = useState(isOpen);

  const open = useCallback(() => setOpened(true), [setOpened]);
  const close = useCallback(() => setOpened(false), [setOpened]);
  const toggle = useCallback(() => setOpened(!opened), [opened, setOpened]);

  return [opened, open, close, toggle];
}

function useContainerSize(
  container: RefObject<HTMLElement | null>,
  onResize?: () => void,
): [width: number, height: number] {
  const [[width, height], setWidthHeight] = useState([0, 0]);

  useEffect(() => {
    if (!container.current) return () => {};
    const localContainer = container.current;

    function resize() {
      if (localContainer) {
        const width = localContainer.clientWidth;
        const height = localContainer.clientHeight;
        onResize?.();
        setWidthHeight([width, height]);
      }
    }

    const to = setTimeout(resize, 100);
    window.addEventListener('resize', resize);
    const resizeObserver = new ResizeObserver(resize);

    resizeObserver.observe(localContainer);

    return () => {
      if (localContainer) {
        resizeObserver.unobserve(localContainer);
      }
      to && clearTimeout(to);
      window.removeEventListener('resize', resize);
    };
  }, [container, onResize]);

  return [width, height];
}

function useElevation({ added, elevated }: { added?: boolean; elevated?: boolean }) {
  return useMemo(() => elevation({ added, elevated }), [added, elevated]);
}

export const UIHooks = {
  useContainerSize,
  useElevation,
  useHover,
  useOpenCloseState,
};
